import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { useCompanies, useSaveOutlet, useUploadOutletLogo } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import type { Outlet } from '@/api/types'
import { ErrorBanner } from '@/components/common'

/** Four letters for the bank, a fixed zero, six characters for the branch. */
const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/

const OUTLET_TYPES = ['RESTAURANT', 'BANQUET', 'CENTRAL_KITCHEN', 'CLOUD_KITCHEN']

type Draft = {
  code: string
  name: string
  outletType: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  phone: string
  email: string
  fssaiNumber: string
  logoDocumentId?: string
  bankAccountName: string
  bankName: string
  bankBranch: string
  bankAccountNumber: string
  bankIfsc: string
  billingLocation: boolean
  productionKitchen: boolean
  storageLocation: boolean
}

const BLANK: Draft = {
  code: '',
  name: '',
  outletType: 'RESTAURANT',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: 'Tamil Nadu',
  postalCode: '',
  phone: '',
  email: '',
  fssaiNumber: '',
  bankAccountName: '',
  bankName: '',
  bankBranch: '',
  bankAccountNumber: '',
  bankIfsc: '',
  billingLocation: true,
  productionKitchen: false,
  storageLocation: false,
}

/** Creates or corrects one outlet. */
export function OutletDialog({
  open,
  outlet,
  onClose,
}: {
  open: boolean
  outlet?: Outlet | null
  onClose: () => void
}) {
  const save = useSaveOutlet()
  const uploadLogo = useUploadOutletLogo()
  const can = useAuthStore((state) => state.can)
  const activeCompany = useAuthStore((state) => state.user?.companyId)
  // Only somebody who administers the platform has more than one to choose
  // from; for everybody else it is their own company and there is no question.
  const { data: companies } = useCompanies()
  const [companyId, setCompanyId] = useState('')
  // Every business that may work here. The owner is always among them, so it is
  // not removable; the rest are somebody's decision.
  const [sharedWith, setSharedWith] = useState<string[]>([])
  const [draft, setDraft] = useState<Draft>(BLANK)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoProblem, setLogoProblem] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setCompanyId(outlet?.companyId ?? activeCompany ?? '')
    setSharedWith(outlet?.companyIds ?? [])
    setLogoProblem(null)
    setDraft(
      outlet
        ? {
            code: outlet.code,
            name: outlet.name,
            outletType: outlet.outletType,
            addressLine1: outlet.addressLine1 ?? '',
            addressLine2: outlet.addressLine2 ?? '',
            city: outlet.city ?? '',
            state: outlet.state ?? '',
            postalCode: outlet.postalCode ?? '',
            phone: outlet.phone ?? '',
            email: outlet.email ?? '',
            fssaiNumber: outlet.fssaiNumber ?? '',
            logoDocumentId: outlet.logoDocumentId,
            bankAccountName: outlet.bankAccountName ?? '',
            bankName: outlet.bankName ?? '',
            bankBranch: outlet.bankBranch ?? '',
            bankAccountNumber: outlet.bankAccountNumber ?? '',
            bankIfsc: outlet.bankIfsc ?? '',
            billingLocation: outlet.billingLocation,
            productionKitchen: outlet.productionKitchen,
            storageLocation: false,
          }
        : BLANK,
    )
  }, [open, outlet, activeCompany])

  // Fetched as a blob rather than pointed at with an img src, because the
  // document endpoint is behind the bearer token and a bare src sends none.
  // Revoked on the way out; a dialog opened all afternoon otherwise holds every
  // mark the user has looked at.
  useEffect(() => {
    const id = draft.logoDocumentId
    if (!open || !id) {
      setLogoPreview(null)
      return
    }
    let stale = false
    let url: string | null = null
    api
      .blobUrl(`/api/v1/documents/${id}`)
      .then((fetched) => {
        url = fetched
        if (stale) URL.revokeObjectURL(fetched)
        else setLogoPreview(fetched)
      })
      .catch(() => setLogoPreview(null))
    return () => {
      stale = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [open, draft.logoDocumentId])

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))

  /**
   * Sends the picked file, then holds its id on the draft. Saved with the rest
   * of the form: a mark chosen and then cancelled should not have stuck.
   */
  const chooseLogo = (file?: File | null) => {
    if (!file || !outlet?.id) return
    setLogoProblem(null)
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setLogoProblem('A JPG or a PNG; other formats will not print')
      return
    }
    // A photograph straight off a phone is several megabytes and is being put
    // at the top of every document this branch ever prints.
    if (file.size > 2 * 1024 * 1024) {
      setLogoProblem('Under 2 MB, please')
      return
    }
    uploadLogo.mutate(
      { outletId: outlet.id, file },
      {
        onSuccess: (stored) => set({ logoDocumentId: stored.id }),
        onError: () => setLogoProblem('That could not be uploaded'),
      },
    )
  }

  const ifsc = draft.bankIfsc.trim().toUpperCase()
  const ifscBad = ifsc.length > 0 && !IFSC.test(ifsc)
  const valid =
    Boolean(draft.code.trim() && draft.name.trim() && draft.outletType && (outlet || companyId)) &&
    !ifscBad

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{outlet ? `Edit ${outlet.name}` : 'New outlet'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Settled when the outlet is opened and never again: it carries its
              halls, orders, invoices and staff, and moving it to another
              business would leave all of that filed under a company that never
              sold it. */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              required
              label="Company"
              value={companyId}
              onChange={(event) => setCompanyId(event.target.value)}
              disabled={Boolean(outlet) || !can('platform.admin')}
              helperText={
                outlet
                  ? 'An outlet stays with the business it was opened in'
                  : 'The business this outlet belongs to'
              }
            >
              {(companies ?? []).map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {can('platform.admin') && (companies ?? []).length > 1 ? (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={(companies ?? []).map((company) => company.id)}
                value={sharedWith.includes(companyId) || !companyId
                  ? sharedWith
                  : [companyId, ...sharedWith]}
                onChange={(_event, value) =>
                  // The owner cannot be dropped: an outlet its own business
                  // cannot work at is nobody's.
                  setSharedWith(value.includes(companyId) ? value : [companyId, ...value])
                }
                getOptionLabel={(id) =>
                  companies?.find((company) => company.id === id)?.name ?? id
                }
                renderTags={(value, getTagProps) =>
                  value.map((id, index) => (
                    <Chip
                      size="small"
                      label={companies?.find((company) => company.id === id)?.name ?? id}
                      {...getTagProps({ index })}
                      key={id}
                      disabled={id === companyId}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Businesses that may work here"
                    helperText={
                      sharedWith.filter((id) => id !== companyId).length > 0
                        ? 'Shared. Each keeps its own orders, invoices and tax at this address.'
                        : 'Add another when two of your businesses trade from this same premises'
                    }
                  />
                )}
              />
            </Grid>
          ) : null}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              required
              label="Code"
              value={draft.code}
              onChange={(event) => set({ code: event.target.value.toUpperCase() })}
              helperText="Unique, e.g. OUT11"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Name"
              value={draft.name}
              onChange={(event) => set({ name: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Type"
              value={draft.outletType}
              onChange={(event) => set({ outletType: event.target.value })}
            >
              {OUTLET_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value.replace(/_/g, ' ').toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* The premises. This is what heads every quotation, bill and
              receipt sent from this branch, so it is written here in full
              rather than left to the company's address. */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address line 1"
              value={draft.addressLine1}
              onChange={(event) => set({ addressLine1: event.target.value })}
              helperText="Printed at the head of every document from this branch"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address line 2"
              value={draft.addressLine2}
              onChange={(event) => set({ addressLine2: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={draft.city}
              onChange={(event) => set({ city: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="State"
              value={draft.state}
              onChange={(event) => set({ state: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pin code"
              value={draft.postalCode}
              onChange={(event) => set({ postalCode: event.target.value })}
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Mobile number"
              value={draft.phone}
              onChange={(event) => set({ phone: event.target.value })}
              helperText="The number a customer holding a bill from here rings"
              inputProps={{ inputMode: 'tel' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={draft.email}
              onChange={(event) => set({ email: event.target.value })}
            />
          </Grid>

          {/* The licence this branch cooks under. A food business is licensed
              to a premises, not to a company, and the bill for food carries the
              number of the place it came from. */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="FSSAI licence number"
              value={draft.fssaiNumber}
              onChange={(event) => set({ fssaiNumber: event.target.value })}
              helperText="Fourteen digits. Printed on every bill, quotation and receipt from here."
              inputProps={{ inputMode: 'numeric', maxLength: 20 }}
            />
          </Grid>

          {/* Where a bill from this branch is paid into. Held here rather than
              on the company: a group trading two brands out of two premises
              banks them separately more often than not. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Bank details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed on this branch's quotations and bills. Leave blank if you take cash only.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Account name"
              value={draft.bankAccountName}
              onChange={(event) => set({ bankAccountName: event.target.value })}
              helperText="The name the account is held in"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Bank"
              value={draft.bankName}
              onChange={(event) => set({ bankName: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Branch"
              value={draft.bankBranch}
              onChange={(event) => set({ bankBranch: event.target.value })}
            />
          </Grid>
          {/* Held as typed: an account number carries leading zeroes and is
              never arithmetic. */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Account number"
              value={draft.bankAccountNumber}
              onChange={(event) => set({ bankAccountNumber: event.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="IFSC"
              value={draft.bankIfsc}
              error={ifscBad}
              // Checked here for the same reason as the GSTIN: an IFSC with a
              // character wrong is a transfer that bounces days later, by which
              // time the bill carrying it has gone out.
              helperText={ifscBad ? 'Eleven characters, e.g. HDFC0001234' : 'Eleven characters'}
              onChange={(event) => set({ bankIfsc: event.target.value.toUpperCase().trim() })}
              inputProps={{ maxLength: 11 }}
            />
          </Grid>

          {/* Uploaded against the branch, so it cannot be chosen until there is
              a branch to hold it. */}
          {outlet ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {logoPreview ? (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Branch logo"
                      sx={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No logo
                    </Typography>
                  )}
                </Box>
                <Box>
                  <input
                    ref={picker}
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    onChange={(event) => {
                      chooseLogo(event.target.files?.[0])
                      event.target.value = ''
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={uploadLogo.isPending}
                    onClick={() => picker.current?.click()}
                  >
                    {uploadLogo.isPending ? 'Uploading…' : logoPreview ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  {logoPreview ? (
                    <Button
                      size="small"
                      color="inherit"
                      sx={{ ml: 1 }}
                      onClick={() => set({ logoDocumentId: undefined })}
                    >
                      Remove
                    </Button>
                  ) : null}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {logoProblem ??
                      'JPG or PNG, under 2 MB. Printed at the head of every document from this branch.'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.billingLocation}
                  onChange={(event) => set({ billingLocation: event.target.checked })}
                />
              }
              label="Bills from here"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.productionKitchen}
                  onChange={(event) => set({ productionKitchen: event.target.checked })}
                />
              }
              label="Cooks here"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.storageLocation}
                  onChange={(event) => set({ storageLocation: event.target.checked })}
                />
              }
              label="Stores here"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() =>
            save.mutate(
              {
                id: outlet?.id,
                companyId: outlet ? undefined : companyId || undefined,
                body: {
                  code: draft.code.trim(),
                  name: draft.name.trim(),
                  outletType: draft.outletType,
                  addressLine1: draft.addressLine1.trim() || undefined,
                  addressLine2: draft.addressLine2.trim() || undefined,
                  city: draft.city || undefined,
                  state: draft.state || undefined,
                  postalCode: draft.postalCode.trim() || undefined,
                  phone: draft.phone.trim() || undefined,
                  email: draft.email || undefined,
                  fssaiNumber: draft.fssaiNumber.trim() || undefined,
                  logoDocumentId: draft.logoDocumentId,
                  // Told apart from a form that simply did not send one: the
                  // absence of a mark has to mean "take it down" only when
                  // there was one to take down.
                  clearLogo: Boolean(outlet?.logoDocumentId) && !draft.logoDocumentId,
                  bankAccountName: draft.bankAccountName.trim() || undefined,
                  bankName: draft.bankName.trim() || undefined,
                  bankBranch: draft.bankBranch.trim() || undefined,
                  bankAccountNumber: draft.bankAccountNumber.trim() || undefined,
                  bankIfsc: ifsc || undefined,
                  companyIds: sharedWith.length > 0 ? sharedWith : undefined,
                  billingLocation: draft.billingLocation,
                  productionKitchen: draft.productionKitchen,
                  storageLocation: draft.storageLocation,
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          {outlet ? 'Save changes' : 'Create outlet'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
